import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  computed,
  effect,
  inject,
} from '@angular/core';
import { Store } from '@ngrx/store';

import * as fromStore from '../../store';

import { getCookiePreferences } from '@teamfoster/sdk/cookie-ngrx';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { initSplitting } from 'src/app/animations/splitting-animation';
import { scrollTriggerClasses } from 'src/app/animations/gsap-animation';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, asapScheduler, filter } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationStart, NavigationEnd, Router } from '@angular/router';
import { LandingPage } from '../../models';
import { HideFooterPayoff, SetNavTheme, SetTheme } from 'src/app/store';

gsap.registerPlugin(ScrollTrigger);

@Component({
  selector: 'app-landing-page',
  templateUrl: './landing-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPageComponent implements AfterViewInit, OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private zone = inject(NgZone);

  landingPage$ = this.store.selectSignal(fromStore.getSelectedLandingPage);
  cookiePreferences$ = this.store.selectSignal(getCookiePreferences);

  private platformId = inject(PLATFORM_ID);

  activeScrollTrigger$ = new BehaviorSubject<string[]>([]);
  scrollSmoother?: ScrollSmoother;

  randomTick = 0;
  scrollTriggerClass = 'scroll-trigger';
  private scrollTriggers: ScrollTrigger[] = [];

  private routerEvents$ = toSignal(this.router.events.pipe(filter(e => [NavigationEnd].some(b => e instanceof b))));

  routerEffect$ = effect(() => {
    const event = this.routerEvents$();

    this.startAnimations();
  });
  landingPageChange$ = effect(() => {
    const event = this.landingPage$();

    this.startAnimations();

    if (isPlatformBrowser(this.platformId)) {
      this.randomTick = parseInt(localStorage.getItem('random-tick-' + event?.slug) || '0');
      this.updateLocalVisited(event);
    }

    asapScheduler.schedule(() => {
      if (event?.slug?.toUpperCase() === 'PLAY') {
        this.store.dispatch(SetNavTheme({ theme: 'light' }));
        this.store.dispatch(SetTheme({ theme: 'dark' }));
        this.store.dispatch(HideFooterPayoff({ hide: true }));
        return;
      }

      this.store.dispatch(SetTheme({ theme: 'light' }));
      this.store.dispatch(HideFooterPayoff({ hide: false }));

      if (event?.header?.darkHeader) {
        this.store.dispatch(SetNavTheme({ theme: 'light' }));
      } else {
        this.store.dispatch(SetNavTheme({ theme: 'dark' }));
      }
    });
  });

  hasHeader$ = computed(() => {
    const page = this.landingPage$();
    return !!page.header?.title;
  });

  isPlayPage$ = computed(() => {
    const page = this.landingPage$();
    return page.slug.toUpperCase() === 'PLAY';
  });

  ngAfterViewInit(): void {
    this.startAnimations();
  }

  startAnimations() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.scrollSmoother?.kill();

      //this.router.events.pipe(filter(e => [NavigationEnd].some(b => e instanceof b))).subscribe(a => {
      // create the scrollSmoother before your scrollTriggers
      this.scrollSmoother = ScrollSmoother.create({
        smooth: 0.3, // how long (in seconds) it takes to "catch up" to the native scroll position
        effects: true, // looks for data-speed and data-lag attributes on elements
        //smoothTouch: 0.1, // much shorter smoothing time on touch devices (default is NO smoothing on touch devices)
      });
    });

    //initGSAPSplit();

    //});

    initSplitting();

    this.activeScrollTrigger$ = scrollTriggerClasses(this.scrollTriggerClass, {
      start: '300px bottom',
      end: '200px top',
      once: true,
    });
  }

  setNavTheme(e: boolean, darkHeader: boolean) {
    if (darkHeader) {
      this.store.dispatch(SetNavTheme({ theme: e ? 'light' : 'dark' }));
    }
  }

  updateLocalVisited(page?: LandingPage) {
    this.randomTick += 1;
    localStorage.setItem('random-tick-' + page?.slug, this.randomTick.toString());
  }

  ngOnDestroy(): void {
    this.scrollSmoother?.kill();
    this.store.dispatch(SetNavTheme({ theme: 'light' }));

    this.scrollTriggers.forEach(trigger => {
      trigger.kill(); // Kill each ScrollTrigger
    });
  }
}
